import { useState } from "react";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import { ArrowUpRight, Mail, Phone, MapPin } from "lucide-react";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useContent } from "../lib/ContentContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Owned Media / XMLOOPS TV",
  "Digital Marketing",
  "Advertising",
  "Creative",
  "Branding",
  "Production",
  "Technology",
  "Not sure yet",
];

const BUDGETS = ["Under ₹10L", "₹10L – ₹50L", "₹50L – ₹2Cr", "₹2Cr+"];

export default function Contact() {
  const { getText } = useContent();
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "",
    service: "", budget: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Thank you — we'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", phone: "", service: "", budget: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check the form and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <Toaster richColors theme="dark" position="bottom-right" />
      <section className="relative pt-40 md:pt-48 pb-16">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -top-32 -left-20 w-[520px] h-[520px] rounded-full bg-[#0057ff] opacity-20 blur-[160px]" />
          <div className="absolute bottom-0 -right-32 w-[520px] h-[520px] rounded-full bg-[#6b11ff] opacity-15 blur-[160px]" />
        </div>

        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="tag-glass" data-testid="contact-overline"><span className="dot" /> Contact · start a conversation</div>
          <h1 className="font-display text-white mt-6 leading-[0.95] tracking-tight text-[13vw] sm:text-[10vw] md:text-[6vw]">
            Tell us about <br />
            <span className="italic-accent text-grad-crimson">your ambition</span>.
          </h1>
          <p className="mt-8 max-w-2xl text-white/70 text-lg md:text-xl leading-relaxed">
            Whether you&rsquo;re launching a category, repositioning a legacy brand
            or scaling into new markets — the fastest way to explore what
            owned-media can do for you is a conversation.
          </p>
        </div>
      </section>

      <section className="pb-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-4 space-y-10">
            <div>
              <div className="tag-glass"><span className="dot" /> Direct</div>
              <ul className="mt-6 space-y-4 text-white/80">
                <li className="flex items-center gap-3"><Mail className="w-4 h-4 text-[#ff5865]" /> {getText("contact.email")}</li>
                <li className="flex items-center gap-3"><Phone className="w-4 h-4 text-[#ff5865]" /> {getText("contact.phone")}</li>
                <li className="flex items-center gap-3"><MapPin className="w-4 h-4 text-[#ff5865]" /> {getText("contact.address")}</li>
              </ul>
            </div>
            <div>
              <div className="tag-glass"><span className="dot" /> Response time</div>
              <p className="mt-3 text-white/70">Within one business day, always.</p>
            </div>
            <div className="glass rounded-2xl p-6">
              <div className="tag-glass"><span className="dot" /> Media kit</div>
              <p className="mt-3 text-white/70 text-sm leading-relaxed">
                Request a private walkthrough of the XMLOOPS TV network,
                screen inventory and case studies.
              </p>
            </div>
          </div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="col-span-12 md:col-span-8 card-lux rounded-2xl p-8 md:p-12"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Full name" required>
                <Input value={form.name} onChange={update("name")} placeholder="Your name" className="bg-transparent border-white/15 h-12 focus-visible:ring-[#e63946]" data-testid="contact-input-name" />
              </Field>
              <Field label="Work email" required>
                <Input type="email" value={form.email} onChange={update("email")} placeholder="you@company.com" className="bg-transparent border-white/15 h-12 focus-visible:ring-[#e63946]" data-testid="contact-input-email" />
              </Field>
              <Field label="Company">
                <Input value={form.company} onChange={update("company")} placeholder="Company / brand" className="bg-transparent border-white/15 h-12 focus-visible:ring-[#e63946]" data-testid="contact-input-company" />
              </Field>
              <Field label="Phone">
                <Input value={form.phone} onChange={update("phone")} placeholder="+91" className="bg-transparent border-white/15 h-12 focus-visible:ring-[#e63946]" data-testid="contact-input-phone" />
              </Field>
              <Field label="Interested in">
                <div className="flex flex-wrap gap-2">
                  {SERVICE_OPTIONS.map((s) => (
                    <button
                      type="button"
                      key={s}
                      onClick={() => setForm((f) => ({ ...f, service: s }))}
                      className={`text-xs px-3 py-2 rounded-full border transition-colors ${
                        form.service === s
                          ? "bg-white text-black border-white"
                          : "border-white/15 text-white/70 hover:border-white/40"
                      }`}
                      data-testid={`contact-service-${s.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Approx. budget">
                <div className="flex flex-wrap gap-2">
                  {BUDGETS.map((b) => (
                    <button
                      type="button"
                      key={b}
                      onClick={() => setForm((f) => ({ ...f, budget: b }))}
                      className={`text-xs px-3 py-2 rounded-full border transition-colors ${
                        form.budget === b
                          ? "bg-white text-black border-white"
                          : "border-white/15 text-white/70 hover:border-white/40"
                      }`}
                      data-testid={`contact-budget-${b.replace(/[^a-zA-Z0-9]+/g, '-')}`}
                    >
                      {b}
                    </button>
                  ))}
                </div>
              </Field>
            </div>

            <div className="mt-6">
              <Field label="Tell us what you're building" required>
                <Textarea
                  value={form.message}
                  onChange={update("message")}
                  placeholder="A few sentences about your brand, goal and timeline."
                  rows={6}
                  className="bg-transparent border-white/15 focus-visible:ring-[#e63946]"
                  data-testid="contact-input-message"
                />
              </Field>
            </div>

            <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
              <p className="text-white/40 text-xs font-mono uppercase tracking-[0.2em]">
                Your details stay with us. Always.
              </p>
              <button
                type="submit"
                disabled={submitting}
                className="btn btn-primary disabled:opacity-60"
                data-testid="contact-submit"
              >
                {submitting ? "Sending…" : sent ? "Send another" : "Send message"}
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </motion.form>
        </div>
      </section>
    </main>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="overline block mb-3">
        {label}{required && <span className="text-[#ff5865] ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}
